// t(id, lang, vars) — the one function every component uses to render a
// user-facing string. No hardcoded English (or Amharic) literal belongs in
// a component; everything routes through this file plus the per-app string
// tables (shared/i18n/common.js + admin.js/portal.js).
//
// Deliberately no i18n library (root CLAUDE.md: "two languages and a few
// hundred strings do not need one") — this is a plain lookup table plus
// `{token}` interpolation, nothing more.
//
// Why `registerStrings()` instead of importing a strings table directly
// here, the way the old single-file shared/i18n/strings.js did: admin/ and
// portal/ each need to ship only THEIR OWN strings (see shared/i18n/
// common.js's header comment for why the old monolithic file blew portal's
// bundle budget). But two components that live in shared/ — PasswordInput
// and SignatureElement — import `t` from this file directly and call it
// unbound (`t(id, lang)`), with no way to receive a strings table as a
// prop. Statically importing admin.js and/or portal.js from this file would
// pull BOTH apps' strings into whichever app imports either shared
// component, defeating the split. Instead, each app composes its own
// {...common, ...appOnly} table once at startup and calls
// registerStrings() with it (see admin/src/i18n.js and portal/src/i18n.js)
// — a plain module-level variable, safe here because admin/ and portal/ are
// two entirely separate Vite builds/bundles (never two live copies of this
// module in one page), and the registration call is a side effect at
// import time, which always runs before the first component renders.
import commonStrings from './common.js';

let activeStrings = commonStrings;

export const LANGUAGES = ['am', 'en'];
export const DEFAULT_LANGUAGE = 'am';

// Called once, at module-eval time, by each app's own src/i18n.js — see
// that file's header comment.
export function registerStrings(strings) {
  activeStrings = strings;
}

// Interpolates `{token}` placeholders in a resolved string with values from
// `vars` (e.g. t('workspace.organizationFallback', lang, { id: 14 })).
// Missing vars are left as the literal `{token}` rather than silently
// dropped — a visible bug is better than a silently wrong sentence.
function interpolate(template, vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match
  );
}

// Looks up `id` for `lang`. If the id is missing entirely, returns a visibly
// broken placeholder (`??id??`) instead of throwing or silently rendering
// nothing — a missing translation should be obvious in review, in either
// language, never a blank UI.
export function t(id, lang, vars) {
  const entry = activeStrings[id];
  if (!entry) {
    console.error(`i18n: missing string id "${id}"`);
    return `??${id}??`;
  }
  const resolvedLang = LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  const template = entry[resolvedLang] ?? entry[DEFAULT_LANGUAGE] ?? `??${id}??`;
  return interpolate(template, vars);
}

// Shared helper for rendering a requests.status / quotations.status enum
// value as a localized label — every screen that shows a status string
// (queue rows, workspace header, later the approval screen) should go
// through this instead of re-deriving `status.${value}` inline.
export function statusLabel(statusValue, lang) {
  if (!statusValue) return '';
  return t(`status.${statusValue}`, lang);
}

export default t;
